import { useEffect, useMemo, useRef } from 'react';
import type { ColumnDef, SortConfig } from './types';

export interface DataGridHeaderProps<T> {
  columns: ColumnDef<T>[];
  sort?: SortConfig;
  onSortChange?: (sort: SortConfig) => void;
  selectable?: boolean;
  rowIds: string[];
  selectedRows: Set<string>;
  onToggleSelectAll?: (checked: boolean) => void;
}

function getNextSort(currentSort: SortConfig | undefined, field: string): SortConfig {
  if (!currentSort || currentSort.field !== field) {
    return { field, direction: 'asc' };
  }

  if (currentSort.direction === 'asc') {
    return { field, direction: 'desc' };
  }

  return { field: '', direction: 'asc' };
}

export function DataGridHeader<T>({
  columns,
  sort,
  onSortChange,
  selectable = false,
  rowIds,
  selectedRows,
  onToggleSelectAll,
}: DataGridHeaderProps<T>) {
  const visibleColumns = useMemo(() => columns.filter((column) => !column.hidden), [columns]);
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  const allSelected = rowIds.length > 0 && rowIds.every((rowId) => selectedRows.has(rowId));
  const someSelected = rowIds.some((rowId) => selectedRows.has(rowId)) && !allSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  return (
    <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800 text-xs uppercase tracking-wide text-gray-600 dark:text-gray-300">
      <tr>
        {selectable && (
          <th scope="col" className="px-4 py-3 w-12 text-left">
            <input
              ref={selectAllRef}
              type="checkbox"
              checked={allSelected}
              onChange={(event) => onToggleSelectAll?.(event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary-700 bg-white dark:bg-gray-900 focus:ring-2 focus:ring-primary-500 focus:outline-none"
              aria-label="Select all rows"
            />
          </th>
        )}
        {visibleColumns.map((column) => {
          const isSorted = sort?.field === column.id && sort.field !== '';
          const sortIndicator = isSorted ? (sort.direction === 'asc' ? '▲' : '▼') : '';
          const sortLabel = isSorted ? `, sorted ${sort.direction}` : '';

          return (
            <th
              key={column.id}
              scope="col"
              className="px-4 py-3 text-left font-semibold whitespace-nowrap"
              style={{ width: column.width, minWidth: column.minWidth }}
            >
              {column.sortable && onSortChange ? (
                <button
                  type="button"
                  onClick={() => onSortChange(getNextSort(sort, column.id))}
                  className="inline-flex items-center gap-1 text-left hover:text-gray-900 dark:hover:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:outline-none rounded"
                  aria-label={`Sort by ${column.header}${sortLabel}`}
                >
                  <span>{column.header}</span>
                  <span className="text-[10px]" aria-hidden="true">
                    {sortIndicator}
                  </span>
                </button>
              ) : (
                <span>{column.header}</span>
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );
}
